/**
 * Service-Area Map Picker (Card 29)
 *
 * A minimal slippy map used to confirm or correct the point a service area was
 * resolved from. The marker is fixed at the centre of the viewport and the map
 * moves under it, which is the standard touch pin-drop: it needs no precise
 * grab target, works identically with a mouse, and there is only one drag
 * implementation to get right. Releasing the drag commits the new coordinate.
 *
 * ── Provider decision (the Card 29 "Gate B" question) ────────────────────────
 * Raster tiles from a URL TEMPLATE, defaulting to OpenStreetMap. There is no
 * API key here, deliberately:
 *   • Vite inlines every `VITE_*` value into the shipped bundle, and a native
 *     build cannot use HTTP-referrer restrictions the way a website can (the
 *     WebView origin is `https://localhost` / `capacitor://localhost`), so a
 *     Google Maps key would be extractable from the `.aab`/`.ipa` with no
 *     effective console restriction behind it. `VITE_GOOGLE_PLACES_API_KEY` is
 *     therefore left unused — see `src/docs/SERVICE_AREA_LOCATION.md` §2.
 *   • Geocoding DOES use Google, but through `/api/locations/reverse` with the
 *     key held server-side, which is why the same objection does not apply
 *     there. Tiles cannot be proxied that way without also proxying every tile
 *     image, so they stay keyless.
 * `VITE_MAP_TILE_URL` overrides the template for deployments that want a paid
 * or self-hosted tile host. Attribution below the map is a licence condition of
 * the OSM default and must not be removed.
 *
 * Tiles are decoration. If they fail to load the pin, the drag and the resolved
 * area all still work — the card's "missing map key must degrade to the typed
 * path, not a broken screen" applies here as "no tiles is not a broken screen".
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MapPin, Minus, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

const TILE_SIZE = 256;
const MIN_ZOOM = 4;
const MAX_ZOOM = 16;

const DEFAULT_TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const DEFAULT_ATTRIBUTION = '© OpenStreetMap contributors';

const tileUrlTemplate = import.meta.env.VITE_MAP_TILE_URL || DEFAULT_TILE_URL;
const tileAttribution = import.meta.env.VITE_MAP_TILE_ATTRIBUTION || DEFAULT_ATTRIBUTION;

// ── Web Mercator ─────────────────────────────────────────────────────────────
// Kept as free functions and exported so the tests can assert the round trip
// rather than the DOM: a projection bug shows up as a pin that drifts on drag,
// which is very hard to see in a snapshot.

export function lngToWorldX(lng: number, zoom: number): number {
  return ((lng + 180) / 360) * TILE_SIZE * 2 ** zoom;
}

export function latToWorldY(lat: number, zoom: number): number {
  const clamped = Math.max(-85.05112878, Math.min(85.05112878, lat));
  const sin = Math.sin((clamped * Math.PI) / 180);
  return (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * TILE_SIZE * 2 ** zoom;
}

export function worldXToLng(x: number, zoom: number): number {
  return (x / (TILE_SIZE * 2 ** zoom)) * 360 - 180;
}

export function worldYToLat(y: number, zoom: number): number {
  const n = Math.PI - (2 * Math.PI * y) / (TILE_SIZE * 2 ** zoom);
  return (180 / Math.PI) * Math.atan(Math.sinh(n));
}

export interface ServiceAreaMapPickerProps {
  latitude: number;
  longitude: number;
  /** Fired once per drag, when the pin settles. */
  onCommit: (latitude: number, longitude: number) => void;
  disabled?: boolean;
  className?: string;
  /** Height of the map viewport in CSS pixels. */
  height?: number;
}

export default function ServiceAreaMapPicker({
  latitude,
  longitude,
  onCommit,
  disabled = false,
  className = '',
  // Tall enough to actually aim with a thumb. At 200 there was nowhere to drag
  // to without immediately running out of map.
  height = 320,
}: ServiceAreaMapPickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height });
  const [zoom, setZoom] = useState(12);
  const [center, setCenter] = useState({ lat: latitude, lng: longitude });
  const [tilesFailed, setTilesFailed] = useState(false);

  const dragRef = useRef<{ pointerId: number; startX: number; startY: number; originX: number; originY: number } | null>(
    null,
  );
  const isDragging = dragRef.current !== null;

  // Follow the coordinate the parent owns, except mid-drag where the pointer is
  // the source of truth (otherwise the pin fights the user).
  useEffect(() => {
    if (dragRef.current) return;
    setCenter({ lat: latitude, lng: longitude });
  }, [latitude, longitude]);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const measure = () => {
      const rect = element.getBoundingClientRect();
      setSize({ width: rect.width, height: rect.height || height });
    };
    measure();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [height]);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (disabled) return;
      const element = containerRef.current;
      if (!element) return;
      element.setPointerCapture?.(event.pointerId);
      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: lngToWorldX(center.lng, zoom),
        originY: latToWorldY(center.lat, zoom),
      };
    },
    [center.lat, center.lng, disabled, zoom],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      event.preventDefault();
      // Dragging right moves the map east-to-west, so the centre moves the
      // opposite way to the pointer.
      const x = drag.originX - (event.clientX - drag.startX);
      const y = drag.originY - (event.clientY - drag.startY);
      setCenter({ lat: worldYToLat(y, zoom), lng: worldXToLng(x, zoom) });
    },
    [zoom],
  );

  const endDrag = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      dragRef.current = null;
      containerRef.current?.releasePointerCapture?.(event.pointerId);
      const moved = Math.abs(event.clientX - drag.startX) > 2 || Math.abs(event.clientY - drag.startY) > 2;
      if (moved) onCommit(center.lat, center.lng);
    },
    [center.lat, center.lng, onCommit],
  );

  const nudge = useCallback(
    (deltaLat: number, deltaLng: number) => {
      if (disabled) return;
      const next = {
        lat: Math.max(-85, Math.min(85, center.lat + deltaLat)),
        lng: center.lng + deltaLng,
      };
      setCenter(next);
      onCommit(next.lat, next.lng);
    },
    [center.lat, center.lng, disabled, onCommit],
  );

  // Keyboard equivalent of the drag. Step size scales with zoom so one press is
  // always roughly the same distance on screen.
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const step = 20 / 2 ** zoom;
      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          nudge(step, 0);
          break;
        case 'ArrowDown':
          event.preventDefault();
          nudge(-step, 0);
          break;
        case 'ArrowLeft':
          event.preventDefault();
          nudge(0, -step);
          break;
        case 'ArrowRight':
          event.preventDefault();
          nudge(0, step);
          break;
      }
    },
    [nudge, zoom],
  );

  const tiles: Array<{ key: string; url: string; left: number; top: number }> = [];
  if (size.width > 0 && size.height > 0) {
    const scale = 2 ** zoom;
    const centerX = lngToWorldX(center.lng, zoom);
    const centerY = latToWorldY(center.lat, zoom);
    const originX = centerX - size.width / 2;
    const originY = centerY - size.height / 2;

    const firstTileX = Math.floor(originX / TILE_SIZE);
    const lastTileX = Math.floor((originX + size.width) / TILE_SIZE);
    const firstTileY = Math.floor(originY / TILE_SIZE);
    const lastTileY = Math.floor((originY + size.height) / TILE_SIZE);

    for (let ty = firstTileY; ty <= lastTileY; ty++) {
      if (ty < 0 || ty >= scale) continue; // No tiles above the pole or below it.
      for (let tx = firstTileX; tx <= lastTileX; tx++) {
        const wrappedX = ((tx % scale) + scale) % scale; // The world repeats east-west.
        tiles.push({
          key: `${zoom}/${tx}/${ty}`,
          url: tileUrlTemplate
            .replace('{z}', String(zoom))
            .replace('{x}', String(wrappedX))
            .replace('{y}', String(ty)),
          left: tx * TILE_SIZE - originX,
          top: ty * TILE_SIZE - originY,
        });
      }
    }
  }

  return (
    <div className={cn('space-y-1', className)}>
      <div
        ref={containerRef}
        role="application"
        aria-label="Map — drag to move the service-area pin"
        tabIndex={disabled ? -1 : 0}
        style={{ height }}
        className={cn(
          'relative w-full overflow-hidden rounded-lg border border-gray-200 bg-gray-100 select-none',
          // `touch-none` (touch-action: none) is load-bearing on mobile, not
          // cosmetic. Without it the browser claims the first finger movement
          // as a page scroll before any pointermove handler runs, fires
          // pointercancel, and the map slides out from under the thumb while
          // the page scrolls instead. preventDefault() cannot win that race —
          // the gesture has to be disclaimed up front in CSS.
          'touch-none overscroll-contain',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
          disabled ? 'opacity-60' : isDragging ? 'cursor-grabbing' : 'cursor-grab',
        )}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={handleKeyDown}
        data-testid="service-area-map"
      >
        {!tilesFailed &&
          tiles.map((tile) => (
            <img
              key={tile.key}
              src={tile.url}
              alt=""
              aria-hidden="true"
              draggable={false}
              width={TILE_SIZE}
              height={TILE_SIZE}
              loading="lazy"
              onError={() => setTilesFailed(true)}
              className="absolute pointer-events-none max-w-none"
              style={{ left: tile.left, top: tile.top, width: TILE_SIZE, height: TILE_SIZE }}
            />
          ))}

        {tilesFailed && (
          <div className="absolute inset-0 flex items-center justify-center px-4 text-center">
            <p className="text-xs text-gray-500">
              Map imagery is unavailable right now. You can still drag to move the pin, or type the
              area above.
            </p>
          </div>
        )}

        {/* The pin. Fixed at the centre — the map moves, not the marker. */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <MapPin
            className="w-8 h-8 text-red-600 drop-shadow-md"
            style={{ transform: 'translateY(-50%)' }}
            aria-hidden="true"
          />
        </div>

        {/* stopPropagation on pointerdown: these sit inside the drag surface,
            so without it every zoom tap also began a map drag. */}
        <div className="absolute right-2 top-2 flex flex-col gap-1.5">
          <button
            type="button"
            aria-label="Zoom in"
            disabled={disabled || zoom >= MAX_ZOOM}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => setZoom((z) => Math.min(MAX_ZOOM, z + 1))}
            className="w-9 h-9 flex items-center justify-center rounded bg-white/95 border border-gray-300 text-gray-700 shadow-sm disabled:opacity-40"
          >
            <Plus className="w-5 h-5" />
          </button>
          <button
            type="button"
            aria-label="Zoom out"
            disabled={disabled || zoom <= MIN_ZOOM}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => setZoom((z) => Math.max(MIN_ZOOM, z - 1))}
            className="w-9 h-9 flex items-center justify-center rounded bg-white/95 border border-gray-300 text-gray-700 shadow-sm disabled:opacity-40"
          >
            <Minus className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] text-gray-500">Drag the map to move the pin.</p>
        {!tilesFailed && <p className="text-[10px] text-gray-400">{tileAttribution}</p>}
      </div>
    </div>
  );
}

export { ServiceAreaMapPicker };
